import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Link as LinkIcon, AlertTriangle, Check, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { updateUserProfile } from '../../services/firestore';

const SettingsPage = () => {
  const { userProfile, currentUser, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gmbUrl, setGmbUrl] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  
  // État pour l'édition du profil
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    business_name: '',
    owner_name: '',
    phone: '',
    business_category: ''
  });

  useEffect(() => {
    if (userProfile) {
      setGmbUrl(userProfile.google_my_business_url || '');
      setProfileForm({
        business_name: userProfile.business_name || '',
        owner_name: userProfile.owner_name || '',
        phone: userProfile.phone || '',
        business_category: userProfile.business_category || 'restaurant'
      });
    }
  }, [userProfile]);

  const handleSaveGmbUrl = async () => {
    if (!currentUser) return;

    // Validate URL format
    if (gmbUrl && !gmbUrl.startsWith('http')) {
      toast.error('Veuillez entrer une URL valide commençant par http:// ou https://');
      return;
    }

    setSaving(true);
    try {
      await updateUserProfile(currentUser.uid, {
        google_my_business_url: gmbUrl
      });
      toast.success('Lien Google My Business sauvegardé!');
    } catch (error) {
      console.error('Error saving GMB URL:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      await updateUserProfile(currentUser.uid, {
        business_name: profileForm.business_name,
        owner_name: profileForm.owner_name,
        phone: profileForm.phone,
        business_category: profileForm.business_category
      });
      
      // Rafraîchir le profil
      if (refreshProfile) {
        await refreshProfile();
      }
      
      toast.success('Profil mis à jour avec succès!');
      setIsEditingProfile(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Erreur lors de la sauvegarde du profil');
    } finally {
      setSaving(false);
    }
  };

  const getEndOfMonth = () => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    lastDay.setHours(23, 59, 59, 999);
    return lastDay;
  };

  const handleCancelSubscription = async () => {
    if (!currentUser) return;

    if (!cancelReason.trim()) {
      toast.error('Veuillez indiquer une raison pour la résiliation');
      return;
    }

    setSaving(true);
    try {
      const endDate = getEndOfMonth();
      
      await updateUserProfile(currentUser.uid, {
        subscription_status: 'cancelled',
        subscription_end_date: endDate.toISOString(),
        cancellation_reason: cancelReason,
        cancelled_at: new Date().toISOString()
      });

      toast.success('Abonnement résilié. Actif jusqu\'au ' + endDate.toLocaleDateString('fr-FR'));
      setShowCancelModal(false);
      setCancelReason('');
      
      // Force page reload to refresh user profile
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Erreur lors de la résiliation');
    } finally {
      setSaving(false);
    }
  };

  if (!userProfile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Paramètres</h2>
        <p className="text-gray-600">Gérez votre profil d'entreprise et la configuration</p>
      </div>

      {/* Business Profile */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Profil d'Entreprise</h3>
          {!isEditingProfile ? (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsEditingProfile(true)}
              className="flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setIsEditingProfile(false);
                  // Reset form
                  setProfileForm({
                    business_name: userProfile?.business_name || '',
                    owner_name: userProfile?.owner_name || '',
                    phone: userProfile?.phone || '',
                    business_category: userProfile?.business_category || 'restaurant'
                  });
                }}
              >
                Annuler
              </Button>
              <Button 
                size="sm" 
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          )}
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Nom de l'Entreprise</Label>
              {isEditingProfile ? (
                <Input 
                  value={profileForm.business_name} 
                  onChange={(e) => setProfileForm({...profileForm, business_name: e.target.value})}
                  className="mt-1" 
                />
              ) : (
                <Input value={userProfile?.business_name || ''} disabled className="mt-1" />
              )}
            </div>
            <div>
              <Label>Catégorie d'Entreprise</Label>
              {isEditingProfile ? (
                <Select 
                  value={profileForm.business_category} 
                  onValueChange={(value) => setProfileForm({...profileForm, business_category: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restaurant">🍽️ Restaurant</SelectItem>
                    <SelectItem value="barbershop">✂️ Barbershop / Coiffure</SelectItem>
                    <SelectItem value="hotel">🏨 Hôtel</SelectItem>
                    <SelectItem value="retail">🛍️ Commerce de détail</SelectItem>
                    <SelectItem value="beauty">💅 Institut de beauté</SelectItem>
                    <SelectItem value="fitness">💪 Fitness / Sport</SelectItem>
                    <SelectItem value="other">📱 Autre</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={userProfile?.business_category || ''} disabled className="mt-1 capitalize" />
              )}
            </div>
          </div>
          
          <div>
            <Label>Nom du Propriétaire</Label>
            {isEditingProfile ? (
              <Input 
                value={profileForm.owner_name} 
                onChange={(e) => setProfileForm({...profileForm, owner_name: e.target.value})}
                className="mt-1" 
              />
            ) : (
              <Input value={userProfile?.owner_name || ''} disabled className="mt-1" />
            )}
          </div>
          
          <div>
            <Label>Email</Label>
            <Input value={userProfile?.email || ''} disabled className="mt-1 bg-gray-50" />
            <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
          </div>
          
          <div>
            <Label>Téléphone</Label>
            {isEditingProfile ? (
              <Input 
                value={profileForm.phone} 
                onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                placeholder="+33 6 12 34 56 78"
                className="mt-1" 
              />
            ) : (
              <Input value={userProfile?.phone || 'Non fourni'} disabled className="mt-1" />
            )}
          </div>
        </div>
      </Card>

      {/* Credits Section */}
      <Card className="overflow-hidden border-2">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-1">Mes Crédits</h3>
              <p className="text-emerald-100 text-sm">SMS et Email marketing</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Credits Display */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-blue-600 font-semibold uppercase tracking-wide">SMS</p>
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <p className="text-4xl font-bold text-blue-900">
                {userProfile?.sms_credits !== undefined ? userProfile.sms_credits : '30'}
              </p>
              <p className="text-xs text-blue-600 mt-1">crédits restants</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-purple-600 font-semibold uppercase tracking-wide">Email</p>
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-4xl font-bold text-purple-900">
                {userProfile?.email_credits !== undefined ? userProfile.email_credits : '30'}
              </p>
              <p className="text-xs text-purple-600 mt-1">crédits restants</p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-400 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900 mb-1">
                  🎁 Crédits gratuits inclus
                </p>
                <p className="text-xs text-amber-800">
                  Vous bénéficiez de 30 SMS et 30 emails gratuits. Au-delà, achetez des crédits supplémentaires selon vos besoins.
                </p>
              </div>
            </div>
          </div>

          {/* Buy Credits Button */}
          <Button 
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-6 text-lg"
            onClick={() => toast.info('Intégration Stripe en cours...')}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Acheter des crédits
          </Button>
        </div>
      </Card>

      {/* Google My Business */}
      <Card className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <LinkIcon className="w-5 h-5 text-[#2E59D7]" />
          Google My Business
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Ajoutez le lien vers votre page Google My Business pour un accès rapide
        </p>
        <div className="space-y-4">
          <div>
            <Label htmlFor="gmb-url">Lien Google My Business</Label>
            <Input 
              id="gmb-url"
              type="url"
              placeholder="https://www.google.com/maps/place/votre-entreprise"
              value={gmbUrl}
              onChange={(e) => setGmbUrl(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Exemple: https://g.page/votre-entreprise ou lien Google Maps
            </p>
          </div>
          
          <Button 
            onClick={handleSaveGmbUrl}
            disabled={saving}
            className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder le lien'}
          </Button>
          
          {gmbUrl && (
            <Button 
              variant="outline"
              onClick={() => window.open(gmbUrl, '_blank')}
              className="ml-2"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Ouvrir Google My Business
            </Button>
          )}
        </div>
      </Card>

      {/* Subscription Info */}
      <Card className="overflow-hidden border-2">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-1">Mon Abonnement</h3>
              <p className="text-blue-100 text-sm">Gérez votre plan et vos préférences</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Plan Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide font-semibold">Plan Actuel</p>
              <p className="text-2xl font-bold text-gray-900 capitalize">
                {userProfile?.subscription_plan || 'Gratuit'}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide font-semibold">Statut</p>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  userProfile?.subscription_status === 'active' ? 'bg-green-500 animate-pulse' :
                  userProfile?.subscription_status === 'trial' ? 'bg-yellow-500 animate-pulse' :
                  userProfile?.subscription_status === 'cancelled' ? 'bg-orange-500' :
                  'bg-gray-400'
                }`}></div>
                <span className={`font-semibold ${
                  userProfile?.subscription_status === 'active' ? 'text-green-700' :
                  userProfile?.subscription_status === 'trial' ? 'text-yellow-700' :
                  userProfile?.subscription_status === 'cancelled' ? 'text-orange-700' :
                  'text-gray-700'
                }`}>
                  {userProfile?.subscription_status === 'active' ? 'Actif' :
                   userProfile?.subscription_status === 'trial' ? 'Essai Gratuit' :
                   userProfile?.subscription_status === 'cancelled' ? 'Résilié' :
                   'Inactif'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Cancellation Warning */}
          {userProfile?.subscription_status === 'cancelled' && userProfile?.subscription_end_date && (
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-orange-900 mb-1">
                    Abonnement en cours de résiliation
                  </p>
                  <p className="text-sm text-orange-800">
                    Vos services resteront actifs jusqu'au{' '}
                    <strong className="font-bold">
                      {new Date(userProfile.subscription_end_date).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </strong>
                  </p>
                  <p className="text-xs text-orange-700 mt-2">
                    Après cette date, vous ne pourrez plus accéder à votre compte.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Features List */}
          <div className="bg-blue-50 rounded-xl p-5">
            <p className="text-sm font-semibold text-gray-900 mb-3">Fonctionnalités incluses :</p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-500" />
                Jeux & Fidélisation illimités
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-500" />
                Réponses IA aux avis
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-500" />
                SMS & Email marketing
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-500" />
                Tableau de bord analytics
              </li>
            </ul>
          </div>

          {/* Action Button */}
          {userProfile?.subscription_status !== 'cancelled' ? (
            <Button 
              variant="outline" 
              className="w-full border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 font-semibold py-6" 
              onClick={() => setShowCancelModal(true)}
              data-testid="manage-subscription-button"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Résilier mon abonnement
            </Button>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600">
                Abonnement résilié - Contactez le support pour toute question
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <DialogTitle className="text-center text-2xl font-bold text-gray-900">
              Résilier votre abonnement ?
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              Nous sommes désolés de vous voir partir. Votre abonnement restera actif jusqu'au{' '}
              <span className="font-semibold text-gray-900">
                {getEndOfMonth().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* What you'll lose */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <p className="text-sm font-semibold text-red-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Vous perdrez l'accès à :
              </p>
              <ul className="text-sm text-red-800 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Tous vos jeux et campagnes de fidélisation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Votre base de données clients</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Les réponses IA aux avis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Les statistiques et analytics</span>
                </li>
              </ul>
            </div>

            {/* Reason */}
            <div>
              <Label htmlFor="cancel-reason" className="text-base font-semibold">
                Pourquoi nous quittez-vous ? *
              </Label>
              <Textarea
                id="cancel-reason"
                placeholder="Votre avis nous aide à nous améliorer..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-2 min-h-[100px]"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                Vos retours sont précieux pour améliorer notre service
              </p>
            </div>

            {/* Timeline */}
            <div className="bg-gray-50 rounded-xl p-5">
              <p className="text-sm font-semibold text-gray-900 mb-3">
                📅 Ce qui va se passer :
              </p>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">
                    1
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Aujourd'hui</p>
                    <p className="text-xs text-gray-600">Votre abonnement est marqué comme résilié</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">
                    2
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Jusqu'au {getEndOfMonth().toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-xs text-gray-600">Accès complet à tous vos services</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold text-sm">
                    3
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Après cette date</p>
                    <p className="text-xs text-gray-600">Compte désactivé - plus d'accès possible</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelModal(false);
                setCancelReason('');
              }}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 flex-1"
              onClick={handleCancelSubscription}
              disabled={saving || !cancelReason.trim()}
            >
              {saving ? 'Résiliation en cours...' : 'Confirmer la résiliation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;